<script setup lang="ts">
    import AppSidebar from '@/views/AppSidebar.vue';
    import AppLink from '@/components/AppLink.vue';
    import AppMainView from '@/components/AppMainView.vue';
    import AppFormTitle from '@/components/forms/AppFormTitle.vue';
    import { ROUTES } from '@/router';
    import AppHeading from '@/components/AppHeading.vue';
</script>

<template>
    <v-layout>
        <app-sidebar>
            <router-link :to="{ name: ROUTES.HOME }">
                <app-link icon="mdi-arrow-left">Back to your Cases</app-link>
            </router-link>
        </app-sidebar>

        <app-main-view>
            <app-form-title> Privacy Policy</app-form-title>
            <app-main-card width="800">
                <app-heading class="app-privacy-heading">Introduction</app-heading>
                <div>
                    <p>
                        Formus Labs Limited (<b>we, us, our</b>) complies with the New Zealand
                        Privacy Act 2020 (<b>the NZ Act</b>), the Australian Privacy Act 1988 (<b
                            >the Australian Act</b
                        >) , and other applicable privacy and data protection laws. Personal
                        information is information about an identifiable individual (a natural
                        person).
                    </p>
                    <p>
                        This policy sets out how we will collect, use, disclose and protect personal
                        information on behalf of Formus users for our own purposes.
                    </p>
                    <p>
                        This policy does not limit or exclude any individual’s rights under the New
                        Zealand Act, the Australian Act, or other applicable laws. If you wish to
                        seek further information on the Act, see
                        <a href="https://www.privacy.org.nz" target="_blank">www.privacy.org.nz</a>.
                        If you wish to seek further information on the Australian Act see
                        <a href="https://www.oaic.gov.au" target="_blank">www.oaic.gov.au</a>.
                    </p>
                </div>
                <app-heading class="app-privacy-heading">Changes to this policy</app-heading>
                <div>
                    <p>
                        We may change this policy by uploading a revised policy onto the website or,
                        where the change is material, contacting the relevant individuals. The
                        change will apply from the date that we upload the revised policy or as
                        otherwise advised.
                    </p>
                    <p><b>This policy was last updated on 26 May 2025</b></p>
                </div>
                <app-heading class="app-privacy-heading"
                    >What personal information do we collect
                </app-heading>
                <div>
                    <div>
                        The Formus platform is an AI-driven software platform for pre-operative
                        planning for joint replacement surgeries.
                    </div>
                    <div class="mt-2">
                        We process two categories of personal information:
                        <ul>
                            <li>
                                <b>Patient Data</b> is personal information about patients that
                                Formus users store using, or input into, the Formus platform. We
                                process this information on behalf of these Formus Users. This may
                                include:
                                <ul>
                                    <li>name</li>
                                    <li>gender</li>
                                    <li>date of birth</li>
                                    <li>medical images, such as CT scans</li>
                                </ul>
                            </li>
                            <li>
                                <b>Account and Marketing Data</b> is personal information that we
                                collect about individuals:
                                <ul>
                                    <li>
                                        who contact us directly (e.g. telephone call, website
                                        enquiry form, email or through our user dashboard) or visit
                                        our website
                                    </li>

                                    <li>
                                        who ask to receive information about us, the Formus platform
                                        or our other services or sign up to our newsletter
                                    </li>

                                    <li>
                                        who sign up to or access or use the Formus platform or our
                                        other services
                                    </li>
                                    <li>in connection with the administration of a user account</li>
                                    <li>who participate in customer feedback surveys</li>
                                </ul>
                                <div class="mt-2">
                                    The Account and Marketing Data we collect may include
                                    company/personal names, usernames, phone numbers, physical
                                    addresses, email addresses and any other contact information
                                    provided to us, location, billing information, purchase history,
                                    information about how the individual uses our website, the
                                    Formus platform or our other services (for example, traffic
                                    volumes, time spent on pages), IP address and/or other device
                                    identifying data, information contained in correspondence with
                                    us or survey responses and other information required to provide
                                    a service or information requested from us.
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                <app-heading class="app-privacy-heading"
                    >Who do we obtain personal information from
                </app-heading>
                <div>
                    <div>
                        We obtain Patient Data when our users collect Patient Data from their
                        patients and input it into the Formus platform.
                    </div>
                    <div class="mt-2">
                        We collect Account and Marketing Data from:
                        <ul>
                            <li>
                                you, when you provide that personal information to us, including
                                through any registration or subscription process, through any
                                contact with us (e.g. telephone call or email) or when you access or
                                use our website, the Formus platform or our other services
                            </li>
                            <li>
                                third parties (e.g. your employer or our licensees) who ask us to
                                establish a user account for you or the information is publicly
                                available. If possible, we will collect personal information from
                                you directly
                            </li>
                            <li>third parties.</li>
                        </ul>
                    </div>
                    <div class="mt-2">
                        When you visit or use our website, the Formus platform or our other
                        services, we may collect information about you:
                        <ul>
                            <li>
                                by recording clickstream data, which is non-personal information
                                that is recorded when you click anywhere on a webpage and is used
                                for the purposes of collecting, analysing and reporting data about
                                how you use our website, the Formus platform or our other services
                            </li>
                            <li>
                                through the use of cookies, web beacons and similar storage
                                technologies.
                            </li>
                        </ul>
                        <div class="mt-2">
                            You do not have to provide us with the Account and Marketing Data we
                            request, but if you do not provide it we may be unable to provide you
                            with certain services as a result.
                        </div>
                    </div>
                </div>
                <app-heading class="app-privacy-heading"
                    >How we use personal information
                </app-heading>
                <div>
                    <div>
                        We use identifiable Patient Data on behalf of Formus users solely for the
                        purpose of providing the Formus platform to our users.
                    </div>
                    <div class="mt-2">
                        We also retain and use anonymised and non-identifiable Patient Data for our
                        own purposes, namely:
                        <ul>
                            <li>
                                to conduct statistical analysis and identify trends and insights
                            </li>
                            <li>for research and development purposes</li>
                        </ul>
                    </div>
                    <div class="mt-2">
                        We use Account and Marketing Data about you:
                        <ul>
                            <li>to verify your identity</li>
                            <li>
                                to provide our website, the Formus platform or our other services to
                                you
                            </li>
                            <li>
                                to market our services to you, including contacting you
                                electronically (e.g. by text or email for this purpose). You can
                                stop receiving our marketing emails by following the unsubscribe
                                instructions included in those emails
                            </li>
                            <li>to improve the products and services that we provide to you</li>
                            <li>to bill you and to collect money that you owe us</li>
                            <li>
                                to respond to communications from you, including enquiries and
                                complaints
                            </li>
                            <li>
                                to conduct research and statistical analysis (on an anonymised
                                basis)
                            </li>
                            <li>
                                to protect and/or enforce our legal rights and interests, including
                                defending any claim
                            </li>
                            <li>
                                for any other purpose authorised by you, the Act or other applicable
                                law
                            </li>
                            <li>
                                to respond to lawful requests by public authorities, including to
                                meet law enforcement requirements.
                            </li>
                        </ul>
                    </div>
                </div>
                <app-heading class="app-privacy-heading"
                    >Disclosing personal information
                </app-heading>
                <div>
                    <div>
                        We may disclose personal information where necessary to do so for any of the
                        above purposes to:
                        <ul>
                            <li>another company within our group</li>
                            <li>
                                any business that supports our services, including any person that
                                hosts or maintains any underlying IT system or data centre that we
                                use to provide our website, the Formus platform or our other
                                services or that we use to process payments
                            </li>
                            licensees of our software through whom we provide the Formus platform to
                            you
                            <li>other third parties (anonymised statistical information only)</li>
                            <li>
                                a person who can require us to supply personal information (e.g. a
                                regulatory authority)
                            </li>
                            <li>
                                any other person authorised by law (e.g. a law enforcement agency)
                            </li>
                            <li>professional advisers e.g. accountants, lawyers, auditors</li>
                            <li>any other person authorised by the relevant individual</li>
                            <li>
                                any other company in the case of a sale, merger, consolidation,
                                liquidation, reorganisation or acquisition.
                            </li>
                        </ul>
                    </div>
                    <div class="mt-2">
                        We share information about your use of the website with our trusted
                        analytics partners through the use of cookies, web beacons and similar
                        storage technologies.
                    </div>
                </div>
                <app-heading class="app-privacy-heading"
                    >Protecting personal information
                </app-heading>
                <div>
                    As required by the Act, we will take reasonable steps to keep personal
                    information safe from loss, unauthorised activity, or other misuse. We implement
                    appropriate technical and organisational measures to ensure a level of security
                    appropriate to risks inherent in processing personal information.
                </div>
                <div>
                    If you are a user of the Formus platform or our other services, you can play an
                    important role in keeping personal information secure by maintaining the
                    confidentiality of any password and accounts used in relation to the Formus
                    platform or our other services. Please do not disclose your password to third
                    parties. Please notify us immediately if you suspect there has been any
                    unauthorised use of your account or any other breach of security.
                </div>
                <app-heading class="app-privacy-heading"
                    >Retaining personal information
                </app-heading>
                <div>
                    We retain Patient Data for as long as the relevant Formus user requires it to be
                    held.
                </div>
                <div>
                    We retain Account and Marketing Data for as long as we have a lawful purpose to
                    use it.
                </div>
                <div>
                    Once personal information is no longer required to be retained by us or Formus
                    users we securely destroy it.
                </div>
                <app-heading class="app-privacy-heading"
                    >Accessing and correcting your personal information
                </app-heading>
                <div>
                    If you are a patient of a surgeon or other medical professional that is using
                    the Formus platform and wish to exercise either of the above rights, you should
                    contact your surgeon or medical professional directly. If we receive any
                    requests to access or correct Patient Data held in the Formus platform, we will
                    forward this request to the relevant user (your surgeon or medical
                    professional), unless we are prohibited to do so by law.
                </div>
                <div class="mt-2">
                    If you believe we hold Account and Marketing Data about you and want to exercise
                    either of the above rights, email us at privacy@formuslabs.com. We will need
                    evidence to confirm that you are the individual to whom the personal information
                    relates. Your email should provide evidence of who you are and set out the
                    details of your request (e.g. the personal information, or the correction, that
                    you are requesting). In respect of a request for correction, if we think the
                    correction is reasonable and we are reasonably able to change the personal
                    information, we will make the correction. If we do not make the correction, we
                    will take reasonable steps to note on the personal information that you
                    requested.
                </div>
                <app-heading class="app-privacy-heading">Internet use</app-heading>
                <div class="mt-2">
                    While we take reasonable steps to maintain secure internet connections, if you
                    provide us with personal information over the internet, the provision of that
                    information is at your own risk.
                </div>
                <div class="mt-2">
                    If you follow a link on our website to another site, the owner of that site will
                    have its own privacy policy relating to your personal information. We suggest
                    you review that site’s privacy policy before you provide personal information.
                </div>
                <app-heading class="app-privacy-heading">Cookies</app-heading>
                <div class="mt-2">
                    We use cookies (an alphanumeric identifier that we transfer to your computer’s
                    hard drive so that we can recognise your browser) to monitor your use of the
                    website, the Formus platform and our other services. You may disable cookies by
                    changing the settings on your browser, although this may mean that you cannot
                    use all of the features of the website, the Formus platform or our other
                    services.
                </div>
                <div class="mt-2">
                    We use Google Analytics, a service provided by Google, Inc. (<b>Google</b>), to
                    gather information about how you engage with our website on a unique but
                    anonymous basis. The results are reported to us by Google as aggregate numbers
                    and trends. Information about Google’s cookies is available from:
                    <a href="https://policies.google.com/technologies/types" target="_blank">
                        https://policies.google.com/technologies/types </a
                    >. Google’s applicable privacy policy for Google Analytics cookies is available
                    at:
                    <a
                        href="https://policies.google.com/technologies/partner-sites"
                        target="_blank"
                    >
                        https://policies.google.com/technologies/partner-sites </a
                    >. You can opt out of Google’s collection and processing of data generated by
                    your use of our website by going to
                    <a href="http://tools.google.com/dlpage/gaoptout" target="_blank">
                        http://tools.google.com/dlpage/gaoptout </a
                    >.
                </div>
                <app-heading class="app-privacy-heading">Contact us</app-heading>
                <div>
                    If you have any questions about this privacy policy, our privacy practices, or
                    if you would like to request access to, or correction of, your personal
                    information, you can contact us here:
                    <a href="mailto:privacy@formuslabs.com" target="_blank">
                        privacy@formuslabs.com </a
                    >.
                </div>
                <div>
                    If you have a complaint about privacy please contact us on the above email so we
                    can address your concerns. You are also entitled to make a complaint directly to
                    the Office of the Privacy Commissioner in NZ (www/privacy.org.nz), the Office of
                    the Australian Privacy Commissioner in Australia (
                    <a href="https://www.oaic.gov.au" target="_blank"> www.oaic.gov.au </a>
                    ), or the appropriate regulator in other countries.
                </div>
            </app-main-card>
        </app-main-view>
    </v-layout>
</template>

<style scoped lang="scss">
    a {
        color: rgb(var(--v-theme-primary));
        text-decoration: underline;
    }

    .app-privacy-heading {
        margin: 8px 0;
    }

    ul {
        margin-left: 24px;
    }

    li,
    p,
    div {
        font-size: 0.875rem;
        font-weight: 400;
        line-height: 1.375rem;
        letter-spacing: 0.0071428571em;
    }

    p {
        margin-bottom: 16px;
    }
</style>
